/**
 * Tickets, and the disputes that are a kind of ticket.
 *
 * ---------------------------------------------------------------------------
 * A DISPUTE IS A TICKET WITH A HIGHER EVIDENCE BAR
 * ---------------------------------------------------------------------------
 * It is not a separate object. A billing question and "the work was not done
 * properly" both end up in the same queue, with the same thread and the same
 * statuses - what differs is that a dispute is raised against a *completed*
 * booking, carries evidence, and can be **rejected** as well as resolved.
 *
 * Modelling them as one thing keeps a single list ("My requests") rather than
 * two places to look for the answer, which is what people actually do when
 * they cannot remember which kind of problem they reported.
 *
 * ---------------------------------------------------------------------------
 * A REJECTION MUST CARRY ITS REASONING
 * ---------------------------------------------------------------------------
 * `resolutionNotes` is required on both endings, not just the happy one. A
 * dispute closed with "Rejected" and nothing else does not end the dispute, it
 * escalates it - see US-11.13. The thread screen refuses to render an outcome
 * without them.
 */

import { IconName } from '../components/ui/Icon';

export type TicketCategory =
  | 'billing'
  | 'dispute'
  | 'service'
  | 'account'
  | 'other';

export type TicketStatus = 'open' | 'in_review' | 'resolved' | 'rejected';

export type TicketMessage = {
  id: string;
  from: 'you' | 'support';
  body: string;
  when: string;
};

export type Ticket = {
  id: string;
  /** What the customer reads out on the phone. */
  reference: string;
  category: TicketCategory;
  subject: string;
  status: TicketStatus;
  createdOn: string;
  updatedOn: string;
  /** The booking it is about, where there is one. Required for a dispute. */
  bookingRef?: string;
  messages: TicketMessage[];
  /**
   * Why it ended the way it did. Set on `resolved` and `rejected` alike.
   */
  resolutionNotes?: string;
  /**
   * What ops looked at, in the customer's view.
   *
   * Named rather than shown: the customer cannot open the professional's photo
   * proof or the route trail, and a list of what was considered is what makes
   * a rejection read as a decision rather than a shrug.
   */
  evidence?: string[];
};

export const ticketCategories: Array<{
  key: TicketCategory;
  label: string;
  blurb: string;
  icon: IconName;
}> = [
  {
    key: 'billing',
    label: 'Billing',
    blurb: 'A charge, a refund or an invoice',
    icon: 'creditCard',
  },
  {
    key: 'service',
    label: 'Service',
    blurb: 'Scheduling, access or the professional',
    icon: 'wrench',
  },
  {
    key: 'dispute',
    label: 'Dispute a job',
    blurb: 'Work that was completed but not right',
    icon: 'alert',
  },
  {
    key: 'account',
    label: 'Account',
    blurb: 'Sign-in, addresses or your details',
    icon: 'user',
  },
  { key: 'other', label: 'Something else', blurb: 'Anything not listed', icon: 'headset' },
];

export const categoryLabel = (key: TicketCategory): string =>
  ticketCategories.find(item => item.key === key)?.label ?? 'Support';

export const categoryIcon = (key: TicketCategory): IconName =>
  ticketCategories.find(item => item.key === key)?.icon ?? 'headset';

/** Status as the customer should read it, not as the queue names it. */
export const statusLabel: Record<TicketStatus, string> = {
  open: 'Open',
  in_review: 'Being reviewed',
  resolved: 'Resolved',
  rejected: 'Not upheld',
};

/**
 * Tickets on file.
 *
 * One of each ending, on purpose: an open billing question, a dispute that was
 * upheld, and a dispute that was not. The last is the one worth looking at -
 * it is the screen that has to explain itself.
 */
export const seededTickets: Ticket[] = [
  {
    id: 't-3081',
    reference: '#SUP3081',
    category: 'billing',
    subject: 'Charged ₹249 instead of ₹200',
    status: 'in_review',
    createdOn: 'Today, 11:20 AM',
    updatedOn: 'Today, 11:48 AM',
    bookingRef: '#HOM12846',
    messages: [
      {
        id: 'm1',
        from: 'you',
        body: 'The app quoted ₹200 at checkout but my card was charged ₹249. Could you check?',
        when: 'Today, 11:20 AM',
      },
      {
        id: 'm2',
        from: 'support',
        body: 'Thanks for flagging this - we can see both amounts on the order and are checking with the gateway. We will come back to you today.',
        when: 'Today, 11:48 AM',
      },
    ],
  },
  {
    id: 't-2974',
    reference: '#SUP2974',
    category: 'dispute',
    subject: 'Bathroom was not cleaned',
    status: 'resolved',
    createdOn: '14 May 2025',
    updatedOn: '15 May 2025',
    bookingRef: '#HOM12790',
    messages: [
      {
        id: 'm1',
        from: 'you',
        body: 'The kitchen was done well but the bathroom looks untouched.',
        when: '14 May 2025',
      },
      {
        id: 'm2',
        from: 'support',
        body: 'We have reviewed the job photos and agree the bathroom was not completed. A re-clean has been booked at no cost and ₹300 refunded.',
        when: '15 May 2025',
      },
    ],
    resolutionNotes:
      'Job photos show the bathroom in its original state. Re-clean booked for 16 May and ₹300 refunded to the original payment method.',
    evidence: [
      "Professional's job photos",
      'Status timeline with arrival and completion times',
      'Your review photos',
    ],
  },
  {
    id: 't-2810',
    reference: '#SUP2810',
    category: 'dispute',
    subject: 'Professional left early',
    status: 'rejected',
    createdOn: '2 May 2025',
    updatedOn: '3 May 2025',
    bookingRef: '#HOM12655',
    messages: [
      {
        id: 'm1',
        from: 'you',
        body: 'I booked three hours and they were gone in ninety minutes.',
        when: '2 May 2025',
      },
      {
        id: 'm2',
        from: 'support',
        body: 'We have looked at this in detail. The reasoning is in the outcome below.',
        when: '3 May 2025',
      },
    ],
    resolutionNotes:
      'The booking was quoted as "1–2 hrs (estimated)" rather than three hours, and the timeline shows 1 hr 34 mins on site with all listed tasks photographed as complete. On that evidence the visit was delivered as booked, so no refund is due. If anything in the work itself is wrong, send photos and we will reopen this.',
    evidence: [
      'Booked duration on the order (1–2 hrs estimated)',
      'Arrival and completion times from the status timeline',
      "Professional's photos of each completed task",
    ],
  },
];

/**
 * What a dispute tells the customer we will look at, before they send it.
 *
 * Listed up front because a dispute form that asks for evidence without saying
 * what the platform already holds gets photographs of things we can see and
 * nothing about the thing we cannot.
 */
export const disputeEvidence: string[] = [
  'The status timeline, with arrival and completion times',
  "The professional's photos of the finished work",
  'Your chat with them',
  'Any photos you attach here',
];
